
import { useState } from 'react';

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d3qgt5rjk8efvddfb36g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSend.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus(
          'Thank you for your message. We will get back to you soon.'
        );
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        // Try to extract a useful error message if the API returns JSON
        let errorMsg =
          'There was an error sending your message. Please try again.';
        try {
          const errData = await response.json();
          if (errData?.message) {
            errorMsg = errData.message;
          }
        } catch (_) {
          // ignore JSON parse errors
        }
        setSubmitStatus(errorMsg);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus(
        'There was an error sending your message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/ec5205bdd3bfbd1e1d56cad45abd70cb.png"
                  alt="Black Canyon Ventures"
                  className="w-[300px] h-auto"
                />
              </div>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  href="#home"
                  className="text-gray-900 hover:text-gray-600 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  Home
                </a>
                <a
                  href="#about"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  About Us
                </a>
                <a
                  href="#focus"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  Investment Focus
                </a>
                <a
                  href="#podcast"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  RealPharma Podcast
                </a>
                <a
                  href="#contact"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative bg-gradient-to-br from-gray-50 to-white py-20 lg:py-32"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c004d989d9576e742a74650f3ec892bf.jpg')`,
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Investing in the Future of
                <span className="text-blue-600 block">
                  Healthcare Innovation
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Black Canyon Ventures specializes in Angel investments and VC LP
                work, focusing on breakthrough companies in biotech, healthtech,
                medtech, and AI infrastructure that are transforming human health
                and technology.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer text-center whitespace-nowrap"
                >
                  Get in Touch
                </a>
                <a
                  href="#about"
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-gray-400 hover:text-gray-900 transition-colors cursor-pointer text-center whitespace-nowrap"
                >
                  Learn More
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/00e0a152fceea3b0f1e262c29389e97e.jpeg"
                alt="Healthcare Innovation Investment"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Focus Section */}
      <section id="focus" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Investment Focus
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We target high-potential companies across four key sectors that
              are reshaping the future of healthcare and technology.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-microscope-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Biotech
              </h3>
              <p className="text-gray-600">
                Revolutionary biotechnology companies developing novel therapeutics,
                diagnostics, and research tools that advance medical science.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                HealthTech
              </h3>
              <p className="text-gray-600">
                Digital health platforms and technologies that improve patient outcomes,
                healthcare delivery, and medical accessibility.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-stethoscope-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                MedTech
              </h3>
              <p className="text-gray-600">
                Innovative medical devices and equipment that enhance surgical precision,
                patient monitoring, and treatment effectiveness.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                AI Infrastructure
              </h3>
              <p className="text-gray-600">
                Artificial intelligence and machine learning platforms that power
                next-generation healthcare solutions and research capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              About Black Canyon Ventures
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are a specialized investment firm with deep expertise in
              healthcare innovation and technology infrastructure.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Our Mission
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                At Black Canyon Ventures, we believe that the convergence of
                biotechnology, healthcare technology, medical devices, and
                artificial intelligence represents the greatest opportunity to
                improve human health and extend quality of life.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our team combines decades of experience in venture capital,
                healthcare innovation, and technology development. We provide not
                just capital, but strategic guidance and industry connections that
                help our portfolio companies achieve breakthrough success.
              </p>
            </div>
            <div>
              <img
                src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/c603ec64e097314a1e584f81dca852b4.jpeg"
                alt="Our Investment Approach"
                className="w-full h-96 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* Team Section */}
          <div className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Our Leadership Team
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Meet the experienced professionals who guide our investment
              strategy and support our portfolio companies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg-grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/41e0c0e6872b51088d3dc66e2a4a968b.jpeg"
                  alt="Managing Partner"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                Dr. Na-Ri Oh
              </h4>
              <p className="text-blue-600 mb-3">Managing Partner</p>
              <p className="text-gray-600 text-sm">
                Medical Doctor and Former Global Strategy Lead at Gilead Sciences with extensive pharmaceutical commercialization experience.
              </p>
            </div>

            <div className="text-center">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/706ac788e18d637a57989b42bf16bc33.webp"
                  alt="Investment Director"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                Ian Wendt
              </h4>
              <p className="text-blue-600 mb-3">Managing Partner</p>
              <p className="text-gray-600 text-sm">
                25+ years of biopharma commercial leadership including Chief Commercial Officer at Jaguar Health. Deep expertise in building scalable healthcare businesses and executing growth strategies.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* RealPharma Podcast Section */}
      <section id="podcast" className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <img
                  src="https://static.readdy.ai/image/5a9597972276deca2cff8c40711f6f62/a7c483ed19ad2841cfc6c5bab3e3ab87.jpeg"
                  alt="RealPharma Logo"
                  className="w-24 h-24 mr-6 rounded-lg"
                />
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  RealPharma Podcast
                </h2>
              </div>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Stay informed about the latest trends and insights in
                pharmaceutical innovation, biotech breakthroughs, and healthcare
                technology through our featured podcast series.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                RealPharma brings together industry leaders, researchers, and
                innovators to discuss the future of medicine, regulatory
                landscapes, and investment opportunities in the pharmaceutical
                sector.
              </p>
              <a
                href="https://realpharma.co"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Visit RealPharma Podcast
                <i className="ri-external-link-line ml-2"></i>
              </a>
            </div>
            <div className="relative">
              <iframe 
                title="RealPharma: Conversations with Pharma Pathfinders" 
                allowTransparency="true"
                height="315" 
                width="100%" 
                style={{border: 'none', minWidth: 'min(100%, 430px)', height: '315px'}}
                scrolling="no" 
                data-name="pb-iframe-player" 
                src="https://www.podbean.com/player-v2/?i=ctn3c-13b9bfb-pbblog-playlist&pbad=0&share=1&download=1&rtl=0&fonts=Arial&skin=1&font-color=auto&logo_link=episode_page&order=episodic&limit=5&filter=all&ss=bcd3b6878f94d235528ab1ba3e3d596d&btn-skin=654771&size=315" 
                loading="lazy" 
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Contact Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to discuss your innovative healthcare or technology venture?
              We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Get in Touch
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Whether you're an entrepreneur with a groundbreaking
                healthcare innovation, or an investor looking to partner with us,
                we're here to explore opportunities together.
              </p>

              <div className="space-y-6">
                {/* Email contact element removed */}
              </div>
            </div>

            <div>
              <form 
                id="contact-narioh1-only"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Tell us about your project or inquiry"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus && (
                  <div
                    className={`p-4 rounded-lg text-sm ${
                      submitStatus.includes('Thank you')
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {submitStatus}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8">
            <div>
              <p className="text-gray-400 leading-relaxed">
                Investing in the future of healthcare innovation through biotech,
                healthtech, medtech, and AI infrastructure.
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 flex justify-between items-center">
            <div className="text-gray-500">
              © {new Date().getFullYear()} Black Canyon Ventures. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;
